/**
 * monatsabschlussText — die EINE Abbildung Status → Pill-Text/Ton der Seite
 * „Month-end Close" (Plan S2 Fassung 2 §5.2) und die reinen Hilfen dazu.
 *
 * 🔴 Die UI-WOERTER sind englisch (Owner-Regel: Woerter englisch, Formate deutsch).
 *    Die ZIFFERNFORMATE bleiben deutsch — `datumDe` liefert `05.10.2026`,
 *    `zeitpunktDe` `05.10.2026, 02:03`; nur `monatsname` traegt ein englisches
 *    Monatswort („October 2026"), weil ein Monatsname ein Wort ist und keine Ziffer.
 *
 * Page-scoped (kein geteiltes Bauteil, Regel 12): die Seite liest ihre Texte nur
 * von hier, keine Zeile trägt ein eigenes Literal.
 *
 * 🔴 Status nie nur über Farbe (Regel 22 Nr. 2): jede Abbildung liefert ein WORT.
 * 🔴 „built, waiting" (registrierte Prüfung mit `grund_code`) und „not
 *    run" bleiben getrennt — wie `zaehlung.wartet` / `zaehlung.nicht_gelaufen`.
 * 🔴 Unbekannt ist nie 0 (Regel 22 Nr. 3): fehlt ein Wert, steht `KEIN_WERT`.
 */

/** Der Hausstrich für „kein Wert" — als Escape, damit er sich von einem Gedankenstrich im Text unterscheidet. */
export const KEIN_WERT = '—'

const humanize = (w) => String(w ?? '').replace(/_/g, ' ')

/**
 * Pill je Prüfstatus einer registrierten Prüfung (`pruefungen.<MAx>.status`).
 * `grundCode` trennt „built, waiting" von „not run".
 */
export function pruefStatusPill(status, grundCode) {
  switch (status) {
    case 'gruen': return { text: 'checked, no finding', tone: 'ok' }
    case 'befund': return { text: 'Finding', tone: 'warn' }
    case 'gescheitert': return { text: 'could not check', tone: 'err' }
    case 'nicht_gelaufen':
      return grundCode
        ? { text: 'built, waiting', tone: 'plain' }
        : { text: 'not run', tone: 'plain' }
    default:
      // Ein Status, den die Seite nicht kennt, zeigt sein rohes Wort — er verschwindet nicht.
      return { text: status ? humanize(status) : KEIN_WERT, tone: 'plain', unbekannt: true }
  }
}

/**
 * Pill je Cockpit-Zeile (`cockpit.<nummer>`, unverändert aus der Laufzeile).
 * `null` heisst: die Nummer steht im heutigen Register, aber nicht in diesem Lauf.
 */
export function cockpitPill(zeile) {
  if (!zeile) return { text: 'not tracked in this run', tone: 'plain' }
  switch (zeile.art) {
    case 'pruefung': return pruefStatusPill(zeile.status, zeile.grund_code)
    case 'nicht_gelaufen': return { text: 'not automated yet', tone: 'plain' }
    case 'hand': return { text: 'Manual task', tone: 'info' }
    case 'entfaellt': return { text: 'not applicable', tone: 'plain' }
    case 'frage': return { text: 'Owner question open', tone: 'info' }
    default: return { text: zeile.art ? humanize(zeile.art) : KEIN_WERT, tone: 'plain', unbekannt: true }
  }
}

/** Die Begleitzeile einer Cockpit-Zeile: Grund, Schlagzeile oder Kennzahl. */
export function cockpitDetail(zeile, pruefungen) {
  if (!zeile) return ''
  if (zeile.art !== 'pruefung') {
    return zeile.art === 'nicht_gelaufen' ? String(zeile.verweis || '') : ''
  }
  const p = (pruefungen || {})[zeile.verweis] || {}
  if (zeile.status === 'befund') {
    const kennzahlen = Object.entries(zeile)
      .filter(([k]) => !['art', 'verweis', 'status', 'grund_code'].includes(k))
      .map(([k, v]) => `${humanize(k)}: ${v}`)
    return [p.schlagzeile, ...kennzahlen].filter(Boolean).join(' · ')
  }
  // Regel-15 W1: nur der vom Endpunkt übersetzte Grund (`grund_anzeige`, wie die Mail
  // über `mailtext.grund`/`warte_grund`) — nie der rohe `grund` (bei `gescheitert` ist das
  // `str(ex)`), nie ein roher `grund_code`.
  if (zeile.status === 'gescheitert') return p.grund_anzeige || KEIN_WERT
  if (zeile.status === 'nicht_gelaufen') return p.grund_anzeige || (zeile.grund_code ? KEIN_WERT : '')
  return ''
}

/**
 * Der Seitenkopf aus `zaehlung` (Grill B2/B3: sie zählt MA7–MA9 mit). Ohne
 * Zählung kein Urteil — nie „no finding".
 */
export function kopfPill(zaehlung) {
  if (!zaehlung) return { text: KEIN_WERT, tone: 'plain', unbekannt: true }
  const n = (k) => Number(zaehlung[k] || 0)
  if (n('gescheitert') > 0) return { text: 'could not check everything', tone: 'err' }
  if (n('befund') > 0) return { text: 'Finding', tone: 'warn', count: n('befund') }
  if (n('gelaufen') < n('geplant')) return { text: 'no finding in the runs that completed', tone: 'plain' }
  return { text: 'checked, no finding', tone: 'ok' }
}

/** Die Zählung als beschriftete Werte — unbekannt bleibt `KEIN_WERT`, nie 0. */
export const ZAEHLUNG_FELDER = [
  ['gelaufen', 'completed'],
  ['befund', 'with finding'],
  ['gescheitert', 'could not check'],
  ['wartet', 'built, waiting'],
  ['nicht_gelaufen', 'not automated yet'],
  ['hand', 'manual tasks'],
  ['entfaellt', 'not applicable'],
  ['frage', 'owner questions'],
]
export function zaehlwert(zaehlung, feld) {
  if (!zaehlung || zaehlung[feld] === undefined || zaehlung[feld] === null) return KEIN_WERT
  return String(zaehlung[feld])
}

/** `2026-10-05` / ISO-Zeitpunkt → `05.10.2026`; Unlesbares → `KEIN_WERT`. */
export function datumDe(wert) {
  if (!wert) return KEIN_WERT
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(wert))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : KEIN_WERT
}

/** ISO-Zeitpunkt → `05.10.2026, 02:03` (Ortszeit des Betrachters). */
export function zeitpunktDe(wert) {
  if (!wert) return KEIN_WERT
  const d = new Date(wert)
  if (Number.isNaN(d.getTime())) return KEIN_WERT
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** `2026-08` → `August 2026`. Das Monatswort ist ein UI-Wort und damit englisch;
 *  die ZIFFERNFORMATE der Seite (`datumDe`, `zeitpunktDe`) bleiben deutsch. */
export function monatsname(monat) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(monat || ''))
  if (!m) return String(monat || KEIN_WERT)
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
  return d.toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) // format-guard: ok — englischer Monatsname ist ein UI-Wort (Owner 2026-08-01), keine Zahlformatierung; `datumDe`/`zeitpunktDe` bleiben de-DE
}

/** Wie viele Kalendermonate das gespeicherte Fenster berührt. */
export function fensterMonate(von, bis) {
  const a = /^(\d{4})-(\d{2})/.exec(String(von || ''))
  const b = /^(\d{4})-(\d{2})/.exec(String(bis || ''))
  if (!a || !b) return null
  return (Number(b[1]) - Number(a[1])) * 12 + (Number(b[2]) - Number(a[2])) + 1
}

/**
 * Die Fensterzeile (Plan §4.2): nie „as of August" allein, sondern Monat, Stand und das
 * GEPRÜFTE Fenster samt Hinweis, dass die Prüfungen die Monate nicht trennen.
 */
export function fensterzeile(daten) {
  if (!daten || !daten.zeile) return ''
  const n = fensterMonate(daten.fenster_von, daten.fenster_bis)
  const zusatz = n && n > 1
    ? ` (${n === 2 ? 'two' : n} months; the checks do not separate them)`
    : ''
  return `Month ${monatsname(daten.monat)} · as of ${datumDe(daten.stichtag)} · `
    + `checked ${datumDe(daten.fenster_von)}–${datumDe(daten.fenster_bis)}${zusatz}`
}

/** Der Leerzustand ohne Ergebnis-Zeile: der GRUND, nicht nur „nichts da". */
export const KEIN_ERGEBNIS_TEXT = {
  noch_kein_lauf: 'There is no run with a result yet.',
  zukunft: 'This month lies in the future — no run has checked it.',
  nicht_im_fenster:
    'No run with a result has checked this month: it lies before the first run, '
    + 'or every run in that period ended without a result.',
  noch_nicht_geprueft:
    'No run has happened for this month yet. The daily run checks it from the next night on.',
}

/**
 * Die Cockpit-Zeilen je Gruppe, in Registerreihenfolge. Nummern, die nur die
 * Laufzeile kennt, hängen hinten an ihrer Gruppe; eine Gruppe, die das Register
 * nicht kennt, wird hinten angehängt statt verschluckt.
 */
export function gruppieren(daten) {
  const anzeige = (daten && daten.anzeige) || { gruppen: [], titel: {}, reihenfolge: [] }
  const cockpit = (daten && daten.cockpit) || {}
  const nummern = [...(anzeige.reihenfolge || [])]
  for (const n of Object.keys(cockpit)) if (!nummern.includes(n)) nummern.push(n)
  const gruppen = (anzeige.gruppen || []).map((g) => ({ ...g, zeilen: [] }))
  const nach = Object.fromEntries(gruppen.map((g) => [g.nummer, g]))
  for (const n of nummern) {
    const pre = String(n).split('.')[0]
    if (!nach[pre]) {
      nach[pre] = { nummer: pre, titel: `Group ${pre}`, abschnitt: '', unbekannt: true, zeilen: [] }
      gruppen.push(nach[pre])
    }
    nach[pre].zeilen.push({
      nummer: n,
      titel: (anzeige.titel || {})[n] || null,
      zeile: cockpit[n] || null,
    })
  }
  return gruppen
}

/** Je Gruppe: wie viele Befunde / Ausfälle / automatisch geprüft. */
export function gruppenstand(gruppe) {
  const zeilen = gruppe.zeilen || []
  const status = (s) => zeilen.filter((z) => z.zeile && z.zeile.art === 'pruefung' && z.zeile.status === s).length
  return {
    befund: status('befund'),
    gescheitert: status('gescheitert'),
    geprueft: zeilen.filter((z) => z.zeile && z.zeile.art === 'pruefung'
      && ['gruen', 'befund'].includes(z.zeile.status)).length,
    gesamt: zeilen.length,
  }
}
